import { isNull, uniqBy } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { getAvailableDocumentNames } from '../../../admin/documents/store';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Icon } from '../../../shared/icon/Icon';
import { CaretDown, CaretSide } from '../../../shared/icons';
import { RadioList, RadioListOption } from '../../../shared/radio/RadioList';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import styles from '../Reports.module.scss';
import { getReportAgreementTypeIds, getSelectedDocumentNameIds, updateDocumentNameIds, updateReportAgreementTypes } from '../store';

export const DocumentSelect: React.FC = () => {
    const [openAgreementTypeSections, setOpenAgreementTypeSections] = useState<number[]>([]);
    const dispatch = useAppDispatch();
    const allDocumentNames = useAppSelector(getAvailableDocumentNames);
    const reportDocumentNameIds = useAppSelector(getSelectedDocumentNameIds);
    const reportAgreementTypeIds = useAppSelector(getReportAgreementTypeIds);

    const agreementTypeOptions = useMemo(() => uniqBy('value', allDocumentNames
        .filter(({ datasetId, agreementTypeId }) => !isNull(datasetId) && !isNull(agreementTypeId))
        .map(({ agreementType, agreementTypeId }) => ({ value: agreementTypeId!.toString(), label: agreementType! }))), [allDocumentNames]
    );

    const getDocumentNameOptions = useCallback((agreementTypeId: number) => allDocumentNames
        .filter(({ datasetId, agreementTypeId }) => !isNull(datasetId) && !isNull(agreementTypeId))
        .filter(documentName => documentName.agreementTypeId === agreementTypeId)
        .map(({ documentName, documentNameId }) => ({ value: documentNameId!.toString(), label: documentName! })), [allDocumentNames]
    );

    const getAgreementName = useCallback((agreementTypeId: number) => agreementTypeOptions.find(({ value }) => value === agreementTypeId.toString())?.label || '', [agreementTypeOptions]);

    const selectedAgreementTypes = useMemo(() => reportAgreementTypeIds.length > 0 ? agreementTypeOptions.filter(({ value }) => reportAgreementTypeIds.includes(parseInt(value))) : null, [agreementTypeOptions, reportAgreementTypeIds]);

    const setSelectedAgreementTypes = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value: string[] | null = null;
        if (!isNull(dropdownValue) && (dropdownValue as Options<DropdownOption>).length > 0) {
            value = (dropdownValue as Options<DropdownOption>).map(({ value }) => value);
        }
        dispatch(updateReportAgreementTypes(value));
    }, [dispatch]);

    const getDocumentNameRadioOptions = useCallback((agreementTypeId: number): RadioListOption[] => {
        const documentNameOptions = getDocumentNameOptions(agreementTypeId);
        const getIsDisabled = (value: string) => documentNameOptions.filter(({ value }) => reportDocumentNameIds.includes(value)).length === 1 && reportDocumentNameIds.includes(value);
        return documentNameOptions.map(({ value, label }) => ({
            id: value,
            value,
            label,
            isSelected: reportDocumentNameIds.includes(value),
            disabled: getIsDisabled(value)
        }));
    }, [getDocumentNameOptions, reportDocumentNameIds]);

    const toggleDocumentName = useCallback((documentNameId: string, agreementTypeId: number) => {
        const isCurrentlyIncluded = reportDocumentNameIds.includes(documentNameId);
        const updatedDocumentNameIds = isCurrentlyIncluded ? reportDocumentNameIds.filter(reportDocumentNameId => reportDocumentNameId !== documentNameId) : [...reportDocumentNameIds, documentNameId];
        dispatch(updateDocumentNameIds(updatedDocumentNameIds, agreementTypeId));
    }, [dispatch, reportDocumentNameIds]);

    const getSectionOpen = useCallback((agreementTypeId: number) => openAgreementTypeSections.includes(agreementTypeId), [openAgreementTypeSections]);
    const toggleAgreementTypeSection = useCallback((agreementTypeId: number) => {
        const sectionOpen = getSectionOpen(agreementTypeId);
        sectionOpen ? setOpenAgreementTypeSections(openAgreementTypeSections.filter(openAgreementTypeId => openAgreementTypeId !== agreementTypeId)) : setOpenAgreementTypeSections([...openAgreementTypeSections, agreementTypeId]);
    }, [getSectionOpen, openAgreementTypeSections]);
    const getSectionOpenIcon = useCallback((isOpen: boolean) => isOpen ? CaretDown : CaretSide, []);

    return (
        <div className={styles.selectWrapper}>
            <div className={styles.agreementTypeWrapper}>
                <div className={styles.agreementTypeHeader} data-testid='generate-report-select-agreement-type-label'>Select the agreement types you would like to generate a report for</div>
                <Dropdown
                    value={selectedAgreementTypes}
                    onChange={setSelectedAgreementTypes}
                    options={agreementTypeOptions}
                    placeholder='Select...'
                    testId='generate-report-select-agreement-type'
                    isMulti
                />
            </div>
            {!isNull(reportAgreementTypeIds) &&
                <div className={styles.documentNamesWrapper}>
                    <Scrollable>
                        {reportAgreementTypeIds.map(agreementTypeId => {
                            const agreementType = getAgreementName(agreementTypeId);
                            const options = getDocumentNameRadioOptions(agreementTypeId);
                            const numberSelected = options.filter(({ isSelected }) => isSelected).length;
                            const totalOptions = options.length;
                            const sectionOpen = getSectionOpen(agreementTypeId);
                            const sectionOpenIcon = getSectionOpenIcon(sectionOpen);
                            return (
                                <div key={agreementTypeId} className={styles.selectedAgreementTypeWrapper}>
                                    <div className={styles.selectedAgreementTypeHeader} onClick={() => toggleAgreementTypeSection(agreementTypeId)}>
                                        <div className={styles.selectedAgreementTypeSectionIcon}><Icon icon={sectionOpenIcon} fontSize={16} /></div>
                                        <div className={styles.selectedAgreementTypeLabel}>{agreementType} ({numberSelected} / {totalOptions})</div>
                                    </div>
                                    {sectionOpen &&
                                        <div className={styles.agreementTypeDocumentNames}>
                                            <RadioList
                                                options={options}
                                                onChange={documentNameId => toggleDocumentName(documentNameId, agreementTypeId)}
                                                testId={`document-report-select-document-name-${agreementTypeId}`}
                                            />
                                        </div>
                                    }
                                </div>
                            );
                        })}
                    </Scrollable>
                </div>
            }
        </div>
    );
};
